import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { BreadcrumbItem, ProductInfo, SchemaType, WebsiteInfo } from '../models/structured-data.models';

@Injectable({
	providedIn: 'root',
})
export class StructuredDataService {
	constructor(
		@Inject(PLATFORM_ID) private platformId: object,
		@Inject(DOCUMENT) private _document: Document,
	) {}

	private get document(): Document {
		if (isPlatformBrowser(this.platformId)) {
			return this._document;
		} else {
			console.error('Document is not available on non-browser platforms.');
			return null;
		}
	}

	private getAll(): NodeListOf<HTMLScriptElement> {
		return this.document.querySelectorAll(`script.structured-data`);
	}

	private get(type: SchemaType): HTMLScriptElement {
		return this.document.querySelector(`script.structured-data.${type}`);
	}

	removeAll(): void {
		this.getAll().forEach((element) => element.remove());
	}

	remove(...types: SchemaType[]): void {
		for (const type of types) {
			this.get(type)?.remove();
		}
	}

	addSchema(schema: Record<string, any>, type: SchemaType): void {
		const existingElement = this.get(type);

		if (existingElement) {
			existingElement.textContent = JSON.stringify(schema);
		} else {
			const newElement = this.document.createElement('script');
			newElement.className = `structured-data ${type}`;
			newElement.type = 'application/ld+json';
			newElement.text = JSON.stringify(schema);
			this.document.head.appendChild(newElement);
		}
	}

	setBreadcrumbSchema(breadcrumbList: BreadcrumbItem[]): void {
		this.addSchema(
			{
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: breadcrumbList.map((item, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					name: item.name,
					item: item.url,
				})),
			},
			'breadcrumb',
		);
	}

	setProductSchema(productInfo: ProductInfo): void {
		this.addSchema(
			{
				'@context': 'https://schema.org',
				'@type': 'Product',
				name: productInfo.name,
				image: productInfo.images,
				url: productInfo.url,
				description: productInfo.description,
				brand: {
					'@type': 'Brand',
					name: productInfo.brand,
				},
				offers: {
					'@type': 'Offer',
					price: productInfo.price,
					priceCurrency: productInfo.currency,
					availability: productInfo.limited ? 'https://schema.org/LimitedAvailability' : 'https://schema.org/InStock',
				},
			},
			'product',
		);
	}

	setWebsiteSchema(websiteInfo: WebsiteInfo): void {
		const websiteSchema: Record<string, any> = {
			'@context': 'https://schema.org',
			'@type': 'Website',
			name: websiteInfo.name,
			url: websiteInfo.url,
		};

		if (websiteInfo.parent) {
			websiteSchema.isPartOf = {
				'@type': 'Website',
				name: websiteInfo.parent.name,
				url: websiteInfo.parent.url,
			};
		}

		if (websiteInfo.searchUrl) {
			websiteSchema.potentialAction = {
				'@type': 'SearchAction',
				target: {
					'@type': 'EntryPoint',
					urlTemplate: `${websiteInfo.searchUrl}{search_term_string}`,
				},
				'query-input': 'required name=search_term_string',
			};
		}

		this.addSchema(websiteSchema, 'website');
	}
}
